// Template authoring form (UI-LAW §4c Authoring archetype). A Template IS a
// saved launch spec (docs/14 §5), so the spec half of this form is literally
// `BatchSpecFields` — the same widgets, in the same order, that author an
// ad-hoc launch. What a Template adds is its own identity: the display name
// the Templates table shows, and the schedule and timezone that control fires.
//
// The receipt is ADVISORY here, unlike the launch form's. A Template is
// authored ahead of the runs it will produce: scenario rows can change, a
// required variable can be declared later, and none of that should stop
// someone saving the spec. Save is gated on the four fixed axes only
// (`isTemplateSaveable`) — the empty spec is the bug this form exists to fix.
//
// Props-pure: the route containers own the reads, the debounced preview, and
// the write.
import type { ReactNode, SyntheticEvent } from "react";

import type { components } from "../../api/schema";
import {
  ResolvedCellView,
  resolvedCellSuppliers,
} from "../variables/ResolvedCellView";
import { BatchSpecFields, labelFor } from "../simulations/BatchSpecFields";
import {
  isTemplateSaveable,
  type SimulationLaunchState,
  type VariableDeclaration,
  variableSweepErrors,
} from "../simulations/simulationLaunchState";
import type { LaunchOptionSets } from "../simulations/useLaunchOptions";
import {
  Badge,
  Button,
  Inline,
  InlineError,
  KeyValue,
  PageHeader,
  Section,
  Stack,
  Text,
  TextInput,
} from "../../ui";

type BatchPreviewResponse = components["schemas"]["BatchPreviewResponse"];

export interface TemplateAuthoringFormProps {
  agentId: string;
  /** Blocking write failure; preview problems stay advisory. */
  errorMessage?: string;
  /** The route's `PageHeader` `leading` slot (e.g. the agent switcher). */
  headerLeading?: ReactNode;
  isPreviewPending: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onChange: (state: SimulationLaunchState) => void;
  onSave: () => void;
  onRetryOptions?: () => void;
  onRetryPreview?: () => void;
  onScheduleChange: (schedule: string) => void;
  onScheduleTimezoneChange: (scheduleTimezone: string) => void;
  options: LaunchOptionSets;
  preview?: BatchPreviewResponse;
  previewErrorMessage?: string;
  schedule: string;
  scheduleTimezone: string;
  /** The template's own name rides `state.displayName`; the spec drops it. */
  state: SimulationLaunchState;
  submitLabel: string;
  variables: VariableDeclaration[];
}

export function TemplateAuthoringForm({
  agentId,
  errorMessage,
  headerLeading,
  isPreviewPending,
  isSaving,
  onCancel,
  onChange,
  onSave,
  onRetryOptions,
  onRetryPreview,
  onScheduleChange,
  onScheduleTimezoneChange,
  options,
  preview,
  previewErrorMessage,
  schedule,
  scheduleTimezone,
  state,
  submitLabel,
  variables,
}: TemplateAuthoringFormProps) {
  const sweepErrors = variableSweepErrors(state.sweepVariables, variables);
  const validationGroups = preview?.validation_groups ?? [];
  const resolvedCells = (preview?.cells ?? []).filter(
    (cell) => Object.keys(cell.variables ?? {}).length > 0,
  );
  const canSave =
    isTemplateSaveable(state) &&
    state.displayName.trim().length > 0 &&
    sweepErrors.length === 0 &&
    !isSaving;

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (canSave) {
      onSave();
    }
  }

  // Route-level Authoring (UI-LAW §4c): Cancel and Save ride the PageHeader
  // inside the form (so `type="submit"` still works), with blocking status
  // immediately left of the buttons. The receipt stays advisory.
  return (
    <form onSubmit={submit}>
      <Stack gap={5}>
        <PageHeader
          actions={
            <>
              {sweepErrors.map((message) => (
                <InlineError key={message}>{message}</InlineError>
              ))}
              {errorMessage !== undefined ? (
                <InlineError>{errorMessage}</InlineError>
              ) : null}
              {onRetryOptions ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onRetryOptions}
                >
                  Retry options
                </Button>
              ) : null}
              <Button
                disabled={isSaving}
                type="button"
                variant="secondary"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button disabled={!canSave} type="submit">
                {isSaving ? "Saving…" : submitLabel}
              </Button>
            </>
          }
          compact
          leading={headerLeading}
        />
        <div className="marker-simulation-create">
          <Stack className="marker-simulation-create__form" gap={6}>
            <Section heading="Template">
              <Stack gap={3}>
                <TextInput
                  label="Display name"
                  required
                  value={state.displayName}
                  onChange={(event) => {
                    onChange({
                      ...state,
                      displayName: event.currentTarget.value,
                    });
                  }}
                />
                <TextInput
                  description="Optional. Use five-field cron or @hourly, @daily, or @weekly."
                  label="Schedule"
                  placeholder="0 9 * * 1-5"
                  value={schedule}
                  onChange={(event) => {
                    onScheduleChange(event.currentTarget.value);
                  }}
                />
                <TextInput
                  description="IANA timezone for scheduled runs. Defaults to UTC."
                  label="Schedule timezone"
                  required
                  value={scheduleTimezone}
                  onChange={(event) => {
                    onScheduleTimezoneChange(event.currentTarget.value);
                  }}
                />
              </Stack>
            </Section>

            <BatchSpecFields
              agentId={agentId}
              connectionOptions={options.connectionOptions}
              onChange={onChange}
              personaOptions={options.personaOptions}
              scenarioSetOptions={options.scenarioSetOptions}
              state={state}
              templateAuthoring
              variables={variables}
              versionOptions={options.versionOptions}
            />
          </Stack>

          <aside className="marker-simulation-create__receipt">
            <Stack gap={4}>
              <Text variant="caps">Saved spec</Text>
              <KeyValue
                rows={[
                  {
                    label: "Version",
                    value: labelFor(
                      options.versionOptions,
                      state.agentVersionId,
                      "Not selected",
                    ),
                  },
                  {
                    label: "Connection",
                    value: labelFor(
                      options.connectionOptions,
                      state.connectionId,
                      "Not selected",
                    ),
                  },
                  {
                    label: "Personas",
                    value:
                      state.personaIds.length === 0
                        ? "Not selected"
                        : `${String(state.personaIds.length)} selected`,
                  },
                  {
                    label: "Scenario set",
                    value: labelFor(
                      options.scenarioSetOptions,
                      state.scenarioSetId,
                      "Not selected",
                    ),
                  },
                  {
                    label: "Sample",
                    value: state.sampleEnabled
                      ? `${state.sampleSize || "?"} of expansion ` +
                        "(fresh seed every scheduled run)"
                      : "Full expansion",
                  },
                ]}
              />
              {/* ASSUMED(Template authoring shows the :preview receipt as
              advisory; Save is gated only on the four fixed axes.) The count
              tells the author how big a run this spec makes today — it is not
              a promise about the day it runs, so nothing here blocks Save. */}
              {isPreviewPending ? (
                <Text variant="muted">Checking this spec…</Text>
              ) : preview !== undefined ? (
                <Stack gap={2}>
                  <Inline align="center" gap={2}>
                    <Text>Runs today</Text>
                    <Badge>{`${String(
                      preview.estimated_usage.simulated_cells,
                    )} runs`}</Badge>
                  </Inline>
                  <Text variant="muted">
                    {preview.expanded_count} expanded · {preview.eligible_count}{" "}
                    eligible
                  </Text>
                </Stack>
              ) : (
                <Text variant="muted">
                  Pick a version, connection, personas, and a scenario set.
                </Text>
              )}
              {previewErrorMessage !== undefined ? (
                <Inline gap={2}>
                  <InlineError>{previewErrorMessage}</InlineError>
                  {onRetryPreview ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onRetryPreview}
                    >
                      Retry preview
                    </Button>
                  ) : null}
                </Inline>
              ) : null}
              {validationGroups.length > 0 ? (
                <Text variant="muted">
                  {validationGroups.length} issue
                  {validationGroups.length === 1 ? "" : "s"} would block a
                  launch today. Saving the template is still allowed.
                </Text>
              ) : null}
              {resolvedCells.map((cell, index) => {
                return (
                  <Stack gap={2} key={cell.cell_key}>
                    <Text variant="caps">
                      Resolved cell {index + 1} of {resolvedCells.length}
                    </Text>
                    <ResolvedCellView
                      supplierLinkTarget="_blank"
                      suppliers={resolvedCellSuppliers(
                        cell,
                        options.personaOptions,
                        options.scenarioOptions,
                      )}
                      variables={cell.variables ?? {}}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </aside>
        </div>
      </Stack>
    </form>
  );
}
